import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Select, Space, message, AutoComplete, Input } from 'antd';
import { UserOutlined, PhoneOutlined, ClockCircleOutlined, SearchOutlined, DownOutlined } from '@ant-design/icons';
import { useCallCenter } from '../CallCenterContext';
import { useSoftphone } from '../../../context/SoftphoneContext';
import DebounceSelect from './DebounceSelect';
import { participantService, pbxService } from '../../../services/api';
import { toE164 } from '../../../utils/phoneFormatter';

const { Text, Title } = Typography;
const { Option } = Select;

const ManualOutboundWidget = () => {
    const {
        hasOutbound,
        campaigns,
        selectedCampaign,
        setSelectedCampaign,
        isInCall,
        isDialing,
        hasContact,
        setHasContact,
        isGettingNext,
        status,
        currentContact,
        setCurrentContact,
        currentCall,
        handleNextContact,
        setIsRescheduleModalVisible,
        handleReleaseContact,
        setManualContactLoaded,
        setCurrentCampaignContactId,
        hasPendingForm,
        setHasPendingForm,
        isRescheduled,
        setIsRescheduled,
        setRescheduleData
    } = useCallCenter();

    const { makeCall } = useSoftphone();

    // Estados locales para campañas Agent-Originated
    const [selectedParticipant, setSelectedParticipant] = useState(null);
    const [selectedPhone, setSelectedPhone] = useState('');
    const [phoneOptions, setPhoneOptions] = useState([]);
    const [isCreatingContact, setIsCreatingContact] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false); // [UX] Auto-collapse

    // [UX] Efecto para auto-colapsar durante llamadas entrantes
    useEffect(() => {
        const isInboundSession = currentCall && currentCall.direction === 'inbound' &&
            (status === 'ringing' || status === 'incall' || status === 'wrapup' || hasPendingForm);

        setIsCollapsed(!!isInboundSession);
    }, [currentCall, status, hasPendingForm]);

    // Efecto para actualizar opciones de teléfono cuando cambia el participante
    useEffect(() => {
        if (selectedParticipant) {
            let phones = [];

            // 1. Intentar obtener array 'phones' (si el backend lo enviara así)
            if (Array.isArray(selectedParticipant.phones)) {
                phones = selectedParticipant.phones;
            }
            // 2. Intentar parsear string 'phone_numbers' (formato backend: "123, 456")
            else if (typeof selectedParticipant.phone_numbers === 'string') {
                phones = selectedParticipant.phone_numbers.split(',').map(p => p.trim()).filter(Boolean);
            }
            // 3. Fallback a propiedad 'phone' simple
            else if (selectedParticipant.phone) {
                phones = [selectedParticipant.phone];
            }

            const options = phones.map(p => ({ value: p }));
            setPhoneOptions(options);

            // Pre-seleccionar el primero si existe
            if (options.length > 0) {
                setSelectedPhone(options[0].value);
            } else {
                setSelectedPhone('');
            }
        } else {
            setPhoneOptions([]);
            if (!selectedPhone) setSelectedPhone('');
        }
    }, [selectedParticipant]);

    if (!hasOutbound) return null;

    // Manejadores locales
    const handleMakeCall = () => {
        const phone = currentContact?.phones?.[0] || currentContact?.phone;
        const options = {};
        if (selectedCampaign && selectedCampaign.type === 'outbound') {
            options.campaignId = selectedCampaign.id;
        }
        if (currentContact && currentContact.id) {
            options.campaignContactId = currentContact.id;
        }
        makeCall(phone, options);
    };

    const handleOpenReschedule = () => {
        setIsRescheduleModalVisible(true);
    };

    const handleHangup = () => {
        window.dispatchEvent(new CustomEvent('softphone-action', { detail: { action: 'end-call' } }));
    };

    // Manejador para origen 'agent'
    const handleCallAgentOrigin = async () => {
        if (!selectedPhone) {
            message.warning("Ingrese un número de teléfono");
            return;
        }

        // --- VALIDACION Y FORMATEO E164 ---
        const e164Phone = toE164(selectedPhone);

        if (!e164Phone) {
            message.error("El número ingresado tiene un formato inválido o no reconocido.");
            return;
        }

        setIsCreatingContact(true);
        try {
            // 1. Crear contacto en backend con numero normalizado
            const response = await pbxService.createAgentOriginContact(
                selectedCampaign.id,
                e164Phone,
                selectedParticipant // Metadata
            );

            if (response.status === 'success') {
                const newContact = response.data;

                // 2. Proteger de limpieza por sync
                if (setManualContactLoaded) setManualContactLoaded(true);

                // 3. Establecer contacto en contexto
                const contactForContext = {
                    ...newContact,
                    name: selectedParticipant ? (selectedParticipant.name || newContact.participant?.name) : 'Contacto Manual',
                    phones: [newContact.phone],
                    is_campaign_contact: true,
                    campaign_contact_id: newContact.id
                };

                setCurrentContact(contactForContext);
                setHasContact(true);

                // [NUEVO] Persistencia para cierre de gestión
                setCurrentCampaignContactId(newContact.id);
                setHasPendingForm(!!selectedCampaign.form_id);
                setIsRescheduled(false);
                setRescheduleData(null);

                // 4. Iniciar llamada
                setTimeout(() => {
                    makeCall(e164Phone, {
                        campaignId: selectedCampaign.id,
                        campaignContactId: newContact.id,
                        participantId: selectedParticipant ? selectedParticipant.id : null
                    });
                }, 100);

                // Limpiar selección
                setSelectedParticipant(null);
                setSelectedPhone('');
            } else {
                message.error("Error al crear contacto: " + (response.message || 'Desconocido'));
            }
        } catch (error) {
            console.error("Error creating agent origin contact:", error);
            message.error("Error de conexión al preparar llamada");
        } finally {
            setIsCreatingContact(false);
        }
    };

    const isAgentOrigin = selectedCampaign?.origen_data === 'agent';

    return (
        <Card
            className="manual-outbound-card"
            id="manual-outbound-card"
            size="small"
            title={<Text strong>Campaña Saliente: {selectedCampaign ? selectedCampaign.name : 'Seleccione...'}</Text>}
            style={{ borderRadius: '12px', border: '1px solid #d9d9d9', marginBottom: 20 }}
            extra={
                <Space>
                    {campaigns.length > 1 && (
                        <Select
                            size="small"
                            value={selectedCampaign?.id}
                            onChange={(val) => {
                                setSelectedCampaign(campaigns.find(c => c.id === val));
                                // Resetear estados locales al cambiar campaña
                                setSelectedParticipant(null);
                                setSelectedPhone('');
                            }}
                            style={{ width: 120 }}
                            placeholder="Cambiar"
                            disabled={isInCall || isDialing || hasContact}
                        >
                            {campaigns.filter(c => c.type === 'outbound').map(c => (
                                <Option key={c.id} value={c.id}>{c.name}</Option>
                            ))}
                        </Select>
                    )}
                    <Button
                        type="text"
                        size="small"
                        icon={<DownOutlined style={{ transition: 'all 0.3s ease-in-out', transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)' }} />}
                        onClick={() => setIsCollapsed(!isCollapsed)}
                    />
                </Space>
            }
        >
            <div style={{
                transition: 'all 0.3s ease-in-out',
                overflow: 'hidden',
                maxHeight: isCollapsed ? 0 : 2000,
                opacity: isCollapsed ? 0 : 1,
                padding: isCollapsed ? 0 : undefined
            }}>
                {!hasContact || (currentCall && currentCall.direction === 'inbound') ? (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        {/* RENDERIZADO CONDICIONAL SEGÚN ORIGEN DE DATOS */}
                        {isAgentOrigin ? (
                            <div style={{ padding: '0 20px', textAlign: 'left' }}>
                                <div style={{ marginBottom: 15 }}>
                                    <Text strong style={{ display: 'block', marginBottom: 5 }}>Buscar Participante (Opcional):</Text>
                                    <DebounceSelect
                                        showSearch
                                        allowClear
                                        placeholder="Buscar por Nombre, DNI o Código..."
                                        fetchOptions={(search, signal) => participantService.search(search, signal).then(res =>
                                            res.data.map(p => ({
                                                label: `${p.name} (${p.document_number || 'S/D'})`,
                                                value: p.id,
                                                participant: p
                                            }))
                                        )}
                                        style={{ width: '100%' }}
                                        onChange={(val) => {
                                            // Con labelInValue, newValue es {value, label, participant}
                                            if (val && val.participant) {
                                                setSelectedParticipant(val.participant);
                                            } else {
                                                setSelectedParticipant(null);
                                                // Si limpiamos participante, ¿limpiamos teléfono?
                                                // Depende del flujo. Si el usuario decide marcar manual, 
                                                // quizás quiera mantener lo escrito. Pero si seleccionó participante
                                                // y luego limpia, el teléfono era del participante.
                                                // Mejor limpiar para estado consistente.
                                                setSelectedPhone('');
                                            }
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: 15, animation: 'fadeIn 0.3s' }}>
                                    <Text strong style={{ display: 'block', marginBottom: 5 }}>
                                        {selectedParticipant ? 'Seleccionar Teléfono:' : 'Ingresar Número a Llamar:'}
                                    </Text>
                                    <AutoComplete
                                        value={selectedPhone}
                                        options={phoneOptions}
                                        onChange={setSelectedPhone}
                                        placeholder="Ingrese número o seleccione..."
                                        style={{ width: '100%' }}
                                    >
                                        <Input />
                                    </AutoComplete>
                                </div>

                                <Button
                                    type="primary"
                                    icon={<PhoneOutlined />}
                                    onClick={handleCallAgentOrigin}
                                    loading={isCreatingContact}
                                    disabled={!selectedPhone || isInCall || isDialing}
                                    block
                                    size="large"
                                >
                                    Iniciar Llamada
                                </Button>
                            </div>
                        ) : (
                            /* CAMPAÑAS NORMALES (BASE DE DATOS) */
                            <>
                                <Button
                                    id="btn-get-next-contact"
                                    className="btn-get-contact"
                                    type="primary"
                                    size="large"
                                    icon={<UserOutlined />}
                                    onClick={handleNextContact}
                                    style={{ borderRadius: '8px', minWidth: '200px' }}
                                    loading={isGettingNext}
                                    disabled={isGettingNext || status !== 'available' || !selectedCampaign || isInCall || isDialing}
                                >
                                    {isGettingNext ? 'Asignando...' : 'Obtener Siguiente Contacto'}
                                </Button>
                                <div style={{ marginTop: 10 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {status === 'available'
                                            ? 'Listo para recibir contactos'
                                            : 'Debe estar DISPONIBLE para solicitar contactos'}
                                    </Text>
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    <div style={{ padding: '10px' }}>
                        <div style={{ textAlign: 'center', marginBottom: 15 }}>
                            <Text type="secondary">Contacto Asignado</Text>
                            <br />
                            <Text strong style={{ fontSize: 16 }}>{currentContact?.name || 'Desconocido'}</Text>
                            {currentContact?.phone && <div><Text type="secondary">{currentContact.phone}</Text></div>}

                            {/* VISUALIZACIÓN DE METADATA */}
                            {currentContact?.metadata && (
                                <div style={{ textAlign: 'left', marginTop: 10, background: '#f5f5f5', padding: '8px', borderRadius: '4px', fontSize: '12px' }}>
                                    <Text strong>Datos de Campaña:</Text>
                                    <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                        {Object.entries(typeof currentContact.metadata === 'string' ? JSON.parse(currentContact.metadata || '{}') : currentContact.metadata)
                                            .filter(([key]) => !['campaign_id', 'reschedule_id', 'scheduled_at'].includes(key))
                                            .map(([key, value]) => (
                                                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                                                    <Text type="secondary" style={{ marginRight: 8 }}>{key}:</Text>
                                                    <Text strong>{value}</Text>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <Space size="small" style={{ width: '100%', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {!isInCall && !isDialing && (
                                <Button
                                    id="btn-call-action"
                                    type="primary"
                                    size="large"
                                    icon={<PhoneOutlined />}
                                    onClick={handleMakeCall}
                                    disabled={!currentContact || status === 'incall' || status === 'wrapup'}
                                    style={{ minWidth: 100 }}
                                >
                                    Llamar
                                </Button>
                            )}

                            <Button
                                icon={<ClockCircleOutlined />}
                                onClick={handleOpenReschedule}
                                disabled={!currentContact || isRescheduled}
                            >
                                Reagendar
                            </Button>

                            {!(isInCall || isDialing) && (
                                <Button
                                    onClick={handleReleaseContact}
                                    disabled={isDialing || status === 'incall' || status === 'wrapup'}
                                >
                                    Cerrar
                                </Button>
                            )}
                        </Space>
                    </div>
                )}
            </div>
        </Card>
    );
};

export default ManualOutboundWidget;
